import React, { useState, useEffect } from 'react';
import { X, Brain, Loader } from 'lucide-react';
import { callGemini } from '../../utils/ai';
import { formatCurrency } from '../../utils/helpers';
import { useLanguage } from '../../contexts/LanguageContext';

export default function AIReportModal({ stats, onClose }) {
  const { t, language } = useLanguage();
  const [report, setReport] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const generateReport = async () => {
      const prompt = `
        Act as a Chief Revenue Officer. Analyze this data for "AAA FM" Event Venue:
        - Total Revenue (Confirmed): ${formatCurrency(stats.totalRevenue)}
        - Potential Revenue (Pipeline): ${formatCurrency(stats.potentialRevenue)}
        - Conversion Rate: ${stats.wonCount}/${stats.total} prequalifications
        - Monthly Trend Data: ${JSON.stringify(stats.trendData.slice(-6))}

        Write a 3-paragraph executive summary:
        1. Financial Health Assessment
        2. Pipeline Opportunity Analysis
        3. One critical recommendation to increase revenue next month.

        Do not use markdown, just plain text paragraphs separated by newlines.
        ${language === 'ur' ? 'Please provide the entire report in Urdu. Use properly formatted localized terminology.' : ''}
      `;
      const text = await callGemini(prompt);
      setReport(text);
      setLoading(false);
    };
    generateReport();
  }, [stats]);

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[90] flex items-center justify-center p-4" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl animate-scale-in overflow-hidden">
        <div className="p-6 bg-gradient-to-r from-purple-600 to-indigo-600 text-white flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Brain className="w-8 h-8 text-purple-200" />
            <div>
              <h2 className="text-xl font-bold">{t('aiExecutiveReport')}</h2>
              <p className="text-purple-200 text-xs">{t('generatedByGemini')}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-full">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-8 min-h-[300px]">
          {loading ? (
            <div className="flex flex-col items-center justify-center h-full space-y-4 opacity-60">
              <Loader className="w-10 h-10 text-purple-600 animate-spin" />
              <p className="text-sm font-medium text-gray-500">
                {t('analyzingFinancialPatterns')}
              </p>
            </div>
          ) : (
            <div className="prose prose-indigo text-gray-700 leading-relaxed whitespace-pre-wrap font-medium text-sm">
              {report}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
